import React, { useEffect, useRef, useState } from "react";
import { MdCancel, MdOutlineCloudUpload } from "react-icons/md";
import {
  useCreateSocietyMutation,
  useDeleteSocietyMutation,
  useGetSocietyByIdQuery,
  useUpdateSocietyMutation,
} from "../../redux/societyApi";
import { useNavigate, useParams } from "react-router-dom";
import ButtonLoading from "../Button loading/ButtonLoading";
import { showConfirmDialog } from "../showConfirmDialog/ShowConfirmDialog";
import { toast } from "react-toastify";
import { formatFileSize, showErrorToast } from "../../utils/Utils";
import { truncateText } from "../Media/Media";
import { ReactSVG } from "react-svg";

const AddHousingSocieties = () => {
  const fileInputRef = useRef(null);
  const [image, setImage] = useState(null);
  const [error, setError] = useState("");
  const inputOnePagerRef = useRef(null);
  const [onePager, setOnePager] = useState(null);
  const [viewOnePager, setViewOnePager] = useState(false);
  const [errorOnePager, setErrorOnePager] = useState("");
  const [createSociety, { isLoading: isCreateLoading }] =
    useCreateSocietyMutation();
  const [updateSociety, { isLoading: isUpdateLoading }] =
    useUpdateSocietyMutation();
  const [deleteSociety, { isLoading: isDeleteLoading }] =
    useDeleteSocietyMutation();
  const [deleteMapId, setDeleteMapId] = useState();
  const [deleteOnePagerId, setDeleteOnePagerId] = useState();
  const [formData, setFormData] = useState({
    title: "",
    location: "",
    currentStatus: "approved",
    mortgageLink: "",
    statusDescription: "",
  });

  const { id } = useParams();
  const isEditMode = Boolean(id);

  const { data, isLoading: isSocietyLoading } = useGetSocietyByIdQuery(id, {
    refetchOnFocus: false,
    skip: !isEditMode,
  });

  useEffect(() => {
    if (isEditMode && data) {
      setFormData({
        title: data?.society?.name,
        location: data?.society?.location,
        currentStatus: data?.society?.status,
        mortgageLink: data?.society?.mortagedPlotsLink,
        statusDescription: data?.society?.statusDescription,
      });

      if (data?.society?.map?.mapUrl) {
        setImage({
          src: data?.society?.map?.mapUrl,
          publicId: data?.society?.map?.publicId,
          file: null,
        });
      }

      if (data?.society?.onePager?.docUrl) {
        setOnePager({
          src: data?.society?.onePager?.docUrl,
          fileType: data?.society?.onePager?.fileType || "Unknown",
          fileSize: data?.society?.onePager?.fileSize || "Unknown",
          fileName: data?.society?.onePager?.fileName || "Unknown",
          file: null,
          publicId: data?.society?.onePager?.publicId,
        });
      }
    }
  }, [isEditMode, data]);

  const [errors, setErrors] = useState({});
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };
  const handleDrop = (e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFile(files[0]);
    }
  };

  const handleFile = (file) => {
    setError("");

    if (!file) {
      setError("No file selected.");
      return;
    }

    if (file.size > 25 * 1024 * 1024) {
      setError("Image size must be less than 25MB.");
      return;
    }

    const objectUrl = URL.createObjectURL(file);
    const img = new Image();

    img.onload = () => {
      const newImage = {
        src: objectUrl,
        file,
      };

      setImage(newImage);
    };

    img.onerror = () => {
      setError("Invalid image file.");
      URL.revokeObjectURL(objectUrl);
    };

    img.src = objectUrl;
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDelete = () => {
    if (image?.publicId) {
      setDeleteMapId(image?.publicId);
    }
    setImage(null);
    setError("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // start one pager
  const handleDropOnePager = (e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleOnePager(files[0]);
    }
  };

  const handleOnePager = (file) => {
    setErrorOnePager("");

    if (!file) {
      setErrorOnePager("No One Pager selected.");
      return;
    }

    const maxFileSize = 25 * 1024 * 1024;
    if (file.size > maxFileSize) {
      setErrorOnePager("File size must be less than 25MB.");
      return;
    }

    const fileType = file.type;
    const objectUrl = URL.createObjectURL(file);

    if (fileType.startsWith("image/")) {
      const img = new Image();

      img.onload = () => {
        const newImage = {
          src: objectUrl,
          fileType: file.type,
          file,
        };
        setOnePager(newImage);
      };

      img.onerror = () => {
        setErrorOnePager("Invalid file.");
        URL.revokeObjectURL(objectUrl);
      };

      img.src = objectUrl;
    } else {
      const newFile = {
        fileName: file.name,
        fileType: file.type,
        fileSize: file.size,
        src: objectUrl,
        file,
      };

      setOnePager(newFile);
    }
  };

  const handleDragOverOnePager = (e) => {
    e.preventDefault();
  };

  const handleDeleteOnePager = () => {
    if (onePager?.publicId) {
      setDeleteOnePagerId(onePager?.publicId);
    }
    setOnePager(null);
    setViewOnePager(false);
    setErrorOnePager("");
    if (inputOnePagerRef.current) {
      inputOnePagerRef.current.value = "";
    }
  };
  // end one pager

  const validateForm = () => {
    const newErrors = {};
    let isValid = true;
    setError("");

    if (!formData?.title?.trim()) {
      newErrors.title = "Title is required.";
      isValid = false;
    }

    if (!formData?.location?.trim()) {
      newErrors.location = "Location is required.";
      isValid = false;
    }

    if (!formData?.currentStatus?.trim()) {
      newErrors.currentStatus = "Current Status is required.";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }

    const societyData = new FormData();
    societyData.append("name", formData.title);
    societyData.append("location", formData.location);
    societyData.append("status", formData.currentStatus);
    societyData.append("statusDescription", formData.statusDescription);
    if (deleteMapId) {
      societyData.append("deleteMapId", deleteMapId);
    }
    if (deleteOnePagerId) {
      societyData.append("deleteOnePagerId", deleteOnePagerId);
    }
    if (formData.mortgageLink) {
      societyData.append("mortagedPlotsLink", formData.mortgageLink);
    }
    if (image?.file) {
      societyData.append("mapImage", image?.file);
    }
    if (onePager?.file) {
      societyData.append("onePager", onePager?.file);
    }

    try {
      let response;
      if (isEditMode) {
        response = await updateSociety({
          id,
          societyData: societyData,
        }).unwrap();
      } else {
        response = await createSociety(societyData).unwrap();
      }
      if (response.success) {
        handleCancel();
        toast.success(response.message);
        navigate("/housing-societies");
      }
    } catch (err) {
      console.error("Society error:", err);
      showErrorToast(err);
    }
  };

  const handleCancel = () => {
    setFormData({
      title: "",
      location: "",
      currentStatus: "",
      mortgageLink: "",
      statusDescription: "",
    });
    setErrors({});
    setImage(null);
    setError("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleDeleteSociety = async (id) => {
    try {
      const response = await deleteSociety(id).unwrap();
      if (response.success) {
        toast.success(response.message);
        navigate("/housing-societies");
      }
    } catch (err) {
      console.error("Society error:", err);
      showErrorToast(err);
    }
  };

  const handleDeleteConfrim = (id) => {
    showConfirmDialog({
      title: "Delete Housing Society",
      message: "Are you sure you want to delete this Society?",
      type: "danger",
      onConfirm: () => {
        handleDeleteSociety(id);
      },
    });
  };

  return (
    <div className="flex flex-col gap-3 bg-white rounded-xl xl:p-8 lg:p-6 p-4 ">
      <h1 className="text-[#05004E] lg:text-[1.8rem] md:text-[1.4rem] text-[1.2rem] font-semibold">
        Add Housing Society
      </h1>
      {isSocietyLoading ? (
        <div className="flex flex-col gap-4">
          <div className=" w-full h-24 rounded-xl bg-[#efefef] animate-pulse"></div>
          <div className=" w-full h-24 rounded-xl bg-[#efefef] animate-pulse"></div>
          <div className=" grid grid-cols-2 gap-6">
            <div className="flex flex-col gap-1">
              <div className="h-6 w-40 bg-[#efefef] animate-pulse rounded-lg"></div>
              <div className=" w-full h-12 rounded-xl bg-[#efefef] animate-pulse"></div>
            </div>{" "}
            <div className="flex flex-col gap-1">
              <div className="h-6 w-40 bg-[#efefef] animate-pulse rounded-lg"></div>
              <div className=" w-full h-12 rounded-xl bg-[#efefef] animate-pulse"></div>
            </div>{" "}
            <div className="flex flex-col gap-1">
              <div className="h-6 w-40 bg-[#efefef] animate-pulse rounded-lg"></div>
              <div className=" w-full h-12 rounded-xl bg-[#efefef] animate-pulse"></div>
            </div>{" "}
            <div className="flex flex-col gap-1">
              <div className="h-6 w-40 bg-[#efefef] animate-pulse rounded-lg"></div>
              <div className=" w-full h-12 rounded-xl bg-[#efefef] animate-pulse"></div>
            </div>
          </div>
          <div className="flex items-center justify-end gap-4">
            <div className="w-[10rem]  rounded-xl h-14 bg-[#efefef] animate-pulse"></div>
            <div className="w-[10rem]  rounded-xl h-14 bg-[#efefef] animate-pulse"></div>
            <div className="w-[10rem]  rounded-xl h-14 bg-[#efefef] animate-pulse"></div>
          </div>
        </div>
      ) : (
        <form
          className="flex flex-col lg:gap-4 md:gap-3 gap-2 lg:text-[1rem] text-[.9rem] "
          onSubmit={handleSubmit}
        >
          <div className="flex flex-col md:gap-1 gap-[2px]">
            <label className="font-semibold">Map Image</label>
            <div
              className="w-full border border-[#00000033] md:p-4 p-3 rounded-xl "
              onDragOver={handleDragOver}
              onDrop={handleDrop}
            >
              <label
                htmlFor="file-upload"
                className="cursor-pointer  flex flex-col md:gap-2 gap-1 items-center w-full "
              >
                <MdOutlineCloudUpload className="text-[#00643b]  lg:size-10 md:size-8 size-6" />
                <p className="text-center font-semibold text-[#0B0B0B] ">
                  Drag your file(s) or{" "}
                  <span className=" text-[#00643b]  ">browse</span>
                </p>

                <p className="text-[#6D6D6D] text-center md:text-sm text-[12px] ">
                  Max 25 MB files are allowed
                </p>
              </label>

              <input
                id="file-upload"
                type="file"
                accept="image/*"
                onChange={(e) => handleFile(e.target.files[0])}
                className="hidden"
                ref={fileInputRef}
              />
              {image && (
                <div className="flex justify-center text-[#6D6D6D] items-center md:gap-4 gap-3 md:text-sm text-[12px] lg:py-[1.3rem] md:py-[1rem] py-[.5rem]">
                  <hr className="border-[#00000033] md:w-[22.22%] w-[30%] border-1" />
                  OR
                  <hr className="border-[#00000033] md:w-[22.22%] w-[30%] border-1" />
                </div>
              )}

              {image && (
                <div className="relative ">
                  <img
                    src={image.src}
                    alt="Upload Preview"
                    className=" object-cover w-full  md:rounded-[1rem] rounded-lg "
                  />
                  <button
                    onClick={() => handleDelete()}
                    className="absolute md:-top-2.5 md:-right-2.5 -top-1.5 -right-1.5 bg-white rounded-full  "
                  >
                    <MdCancel className="lg:size-10 md:size-8 size-6 text-red-500" />
                  </button>
                </div>
              )}
            </div>
            {error && (
              <p className="text-red-500 md:text-sm text-[12px]">{error}</p>
            )}
          </div>

          <div className="flex flex-col md:gap-1 gap-[2px] ">
            <div className="font-semibold ">Upload One Pager</div>
            <div
              className="w-full border border-dashed border-[#00000033] md:p-4 p-3 rounded-xl "
              onDragOver={handleDragOverOnePager}
              onDrop={handleDropOnePager}
            >
              <label
                htmlFor="one-pager-upload"
                className="cursor-pointer  flex flex-col md:gap-2 gap-1 md:py-4 py-2 items-center w-full "
              >
                <div className="md:p-3 p-1.5 bg-[#02643B20] rounded-full">
                  <ReactSVG
                    src={"/icon/upload file.svg"}
                    className={`lg:size-10 md:size-8 size-6`}
                  />
                </div>
                <p className="text-center font-semibold text-[#0B0B0B] ">
                  Drop your files here or{" "}
                  <span className=" text-[#00643b]  ">Click to upload</span>
                </p>

                <p className="text-[#6D6D6D] text-center md:text-sm text-[12px] ">
                  SVG, PNG, JPG, GIF or PDF etc.
                </p>
                <p className="text-[#6D6D6D] md:text-sm text-[12px] text-center">
                  Max file size is 25Mb
                </p>
              </label>

              <input
                id="one-pager-upload"
                type="file"
                accept="
            image/*,
            application/pdf,
            application/msword,
            application/vnd.openxmlformats-officedocument.wordprocessingml.document,
            application/vnd.ms-powerpoint,
            application/vnd.openxmlformats-officedocument.presentationml.presentation,
            application/vnd.ms-excel,
            application/vnd.openxmlformats-officedocument.spreadsheetml.sheet
          "
                onChange={(e) => handleOnePager(e.target.files[0])}
                className="hidden"
                ref={inputOnePagerRef}
              />
              {onePager && (
                <div className="flex justify-center text-[#6D6D6D] items-center md:gap-4 gap-3 md:text-sm text-[12px] lg:py-[1.3rem] md:py-[1rem] py-[.5rem]">
                  <hr className="border-[#00000033] md:w-[22.22%] w-[30%] border-1" />
                  OR
                  <hr className="border-[#00000033] md:w-[22.22%] w-[30%] border-1" />
                </div>
              )}

              {onePager &&
                (onePager?.src && onePager?.fileType.startsWith("image/") ? (
                  <div className="relative">
                    <img
                      src={onePager.src}
                      alt="Upload Preview"
                      className="object-cover w-full md:rounded-[1rem] rounded-lg"
                    />
                    <button
                      onClick={handleDeleteOnePager}
                      className="absolute md:-top-2.5 md:-right-2.5 -top-1.5 -right-1.5 bg-white rounded-full  "
                    >
                      <MdCancel className="lg:size-10 md:size-8 size-6 text-red-500" />
                    </button>
                  </div>
                ) : onePager?.fileType === "application/pdf" ? (
                  <>
                    <div className="flex items-center justify-between gap-2 lg:p-4 md:p-3 p-2 border rounded-lg ">
                      <div className="flex items-center lg:gap-4 md:gap-3 gap-2 ">
                        <ReactSVG
                          src={"/icon/file.svg"}
                          className={`lg:size-10 md:size-8 size-6`}
                        />

                        <div>
                          <p
                            title={onePager.fileName}
                            className="font-medium lg:text-sm md:text-[12px] text-[10px] capitalize "
                          >
                            {truncateText(onePager.fileName, 15)}
                          </p>
                          <p className="lg:text-sm md:text-[12px] text-[10px]">
                            {formatFileSize(onePager.fileSize)}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div
                          className=" md:flex hidden cursor-pointer text-[#00643b] font-medium"
                          onClick={() => setViewOnePager(!viewOnePager)}
                        >
                          Click to {viewOnePager ? "close" : "view"}
                        </div>
                        <div
                          onClick={handleDeleteOnePager}
                          className=" text-red-500 font-medium bg-[#66666620] md:p-2 py-1 px-2  md:text-[1rem] text-[.85rem]  rounded-md cursor-pointer"
                        >
                          Remove
                        </div>
                      </div>
                    </div>
                    {viewOnePager && (
                      <iframe
                        src={onePager.src}
                        width="100%"
                        height="500px"
                        className="rounded-[1rem] mt-2"
                        title="File Preview"
                      />
                    )}
                  </>
                ) : (
                  <div className="flex items-center justify-between gap-2 lg:p-4 md:p-3 p-2 border rounded-lg ">
                    <div className="flex items-center lg:gap-4 md:gap-3 gap-2  max-w-full">
                      <ReactSVG
                        src={"/icon/file.svg"}
                        className={`lg:size-10 md:size-8 size-6 `}
                      />

                      <div>
                        <p
                          title={onePager.fileName}
                          className="font-medium lg:text-sm md:text-[12px] text-[10px] capitalize  "
                        >
                          {truncateText(onePager.fileName, 15)}
                        </p>
                        <p className="lg:text-sm md:text-[12px] text-[10px]">
                          {formatFileSize(onePager.fileSize)}
                        </p>
                      </div>
                    </div>
                    <div
                      onClick={handleDeleteOnePager}
                      className=" text-red-500 font-medium bg-[#66666620] md:p-2 py-1 px-2  md:text-[1rem] text-[.85rem]  rounded-md cursor-pointer"
                    >
                      Remove
                    </div>
                  </div>
                ))}
            </div>
            {errorOnePager && (
              <p className="text-red-500 md:text-sm text-[12px]">
                {errorOnePager}
              </p>
            )}
          </div>

          <div className=" flex items-stretch  w-full lg:gap-6 md:gap-4 gap-2">
            <div className="flex flex-col md:gap-1 gap-[2px] flex-1">
              <label className="font-semibold">Add Name</label>
              <input
                type="text"
                name="title"
                placeholder="Enter name"
                value={formData.title}
                onChange={handleChange}
                className="w-full border border-[#00000033] text-[#737791] outline-none lg:p-4 md:p-3 p-2 lg:rounded-xl rounded-lg"
              />
              {errors.title && (
                <p className="text-red-500 md:text-sm text-[12px]">
                  {errors.title}
                </p>
              )}
            </div>
            <div className="flex flex-col md:gap-1 gap-[2px] flex-1">
              <label className="font-semibold">Add Location</label>
              <input
                type="text"
                name="location"
                placeholder="Enter Location"
                value={formData.location}
                onChange={handleChange}
                className="w-full border border-[#00000033] text-[#737791] outline-none lg:p-4 md:p-3 p-2 lg:rounded-xl rounded-lg"
              />
              {errors.location && (
                <p className="text-red-500 md:text-sm text-[12px]">
                  {errors.location}
                </p>
              )}
            </div>
          </div>
          <div className=" flex items-stretch  w-full lg:gap-6 md:gap-4 gap-2">
            <div className="flex flex-col md:gap-1 gap-[2px]  flex-1">
              <label className="font-semibold">Current Status</label>

              <select
                name="currentStatus"
                value={formData.currentStatus}
                onChange={handleChange}
                className="w-full border border-[#00000033] text-[#737791] outline-none lg:p-4 md:p-3 p-2 lg:rounded-xl rounded-lg"
              >
                <option value="approved">Approved</option>
                <option value="unapproved">Unapproved</option>
                <option value="underProcess">Under Process</option>
                <option value="illegal">Illegal</option>
              </select>
              {errors.currentStatus && (
                <p className="text-red-500 md:text-sm text-[12px]">
                  {errors.currentStatus}
                </p>
              )}
            </div>
            <div className="flex flex-col md:gap-1 gap-[2px] flex-1">
              <label className="font-semibold">Mortgaged Link</label>
              <input
                type="text"
                name="mortgageLink"
                placeholder="Enter Mortgaged Plots Link"
                value={formData.mortgageLink}
                onChange={handleChange}
                className="w-full border border-[#00000033] text-[#737791] outline-none lg:p-4 md:p-3 p-2 lg:rounded-xl rounded-lg"
              />
            </div>
          </div>

          {formData.currentStatus === "approved" && (
            <div className="flex flex-col md:gap-1 gap-[2px] ">
              <label className="font-semibold">Status Description</label>
              <input
                type="text"
                name="statusDescription"
                placeholder="Enter status description"
                value={formData.statusDescription}
                onChange={handleChange}
                className="w-full border border-[#00000033] text-[#737791] outline-none lg:p-4 md:p-3 p-2 lg:rounded-xl rounded-lg"
              />
            </div>
          )}

          <div className="flex items-center justify-end lg:gap-4 md:gap-3 gap-2">
            <p
              onClick={() => handleCancel()}
              className="lg:w-[10rem] md:w-[9rem] w-max md:py-2 md:px-0 py-1 px-2 md:text-[1rem] text-[.85rem]  border  border-[#00643B] text-[#00643B] text-center cursor-pointer  lg:rounded-xl md:rounded-lg rounded-md font-medium"
            >
              Cancel
            </p>
            {data || isEditMode ? (
              <div
                disabled={isDeleteLoading ? true : false}
                onClick={() => handleDeleteConfrim(data?.society?._id)}
                className={`lg:w-[10rem] md:w-[9rem] w-max md:py-2 md:px-0 py-1 px-2 md:text-[1rem] text-[.85rem]  border text-center border-[#D80000] flex items-center justify-center ${
                  isDeleteLoading
                    ? "opacity-50 cursor-not-allowed"
                    : "opacity-100 cursor-pointer"
                } text-white bg-[#D80000] lg:rounded-xl md:rounded-lg rounded-md font-medium`}
              >
                {isDeleteLoading ? (
                  <ButtonLoading text=" Delete..." />
                ) : (
                  "Delete"
                )}
              </div>
            ) : (
              ""
            )}
            <button
              type="submit"
              disabled={isCreateLoading || isUpdateLoading ? true : false}
              className="lg:w-[10rem] md:w-[9rem] w-max md:py-2 md:px-0 py-1 px-2 md:text-[1rem] text-[.85rem]  border border-[#00643B] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center text-white bg-[#00643B] lg:rounded-xl md:rounded-lg rounded-md font-medium"
            >
              {isEditMode ? (
                isUpdateLoading ? (
                  <ButtonLoading text="Update..." />
                ) : (
                  "Update"
                )
              ) : isCreateLoading ? (
                <ButtonLoading text="Add..." />
              ) : (
                "Add"
              )}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default AddHousingSocieties;
